import { Table, Select, Button, Modal, Input, Tag, message } from "antd";
import { useState, useContext, useEffect } from "react";
import dayjs from "dayjs";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";

export default function HodChange() {
    const { authData, backUrl } = useContext(AuthContext);
    const [requests, setRequests] = useState([]);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [filters, setFilters] = useState({ status: null, year: null });

    const [isApproveModalVisible, setIsApproveModalVisible] = useState(false);
    const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);
    const [adminRemarks, setAdminRemarks] = useState("");
    const [selectedRecord, setSelectedRecord] = useState(null);

    const [isSubsModalVisible, setIsSubsModalVisible] = useState(false);
    const [subsRecord, setSubsRecord] = useState(null);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // ✅ Fetch HOD transfer requests
    const fetchData = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/hr/transfer/list/`, {
                headers: { Authorization: `Bearer ${authData.access}` },
            });
            setRequests(res.data.results || res.data || []);
        } catch (error) {
            console.error("Error fetching HOD transfer requests:", error.response || error.message);
            message.error("Failed to load HOD transfer requests.");
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // ✅ Status colors
    const statusColors = {
        PENDING: "orange",
        APPROVED: "green",
        REJECTED: "red",
        CANCELLED: "gray",
    };

    // ✅ Handle Approve
    const handleApprove = async () => {
        if (!selectedRecord?.id) return;
        try {
            const res = await axios.post(
                `${backUrl}/api/emp/hod-replacement/approve/${selectedRecord.id}/`,
                { action: "APPROVE", approver_reason: adminRemarks },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            message.success(res.data?.message || "HOD transfer request approved successfully.");
            setIsApproveModalVisible(false);
            setAdminRemarks("");
            setSelectedRecord(null);
            await fetchData();
        } catch (error) {
            console.error("Error approving request:", error);
            message.error(error.response?.data?.message || "Failed to approve request.");
        }
    };

    // ✅ Handle Reject
    const handleReject = async () => {
        if (!selectedRecord?.id) return;
        try {
            const res = await axios.post(
                `${backUrl}/api/emp/hod-replacement/approve/${selectedRecord.id}/`,
                { action: "REJECT", reason: adminRemarks },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            message.success(res.data?.message || "HOD transfer request rejected.");
            setIsRejectModalVisible(false);
            setAdminRemarks("");
            setSelectedRecord(null);
            await fetchData();
        } catch (error) {
            console.error("Error rejecting request:", error);
            message.error(error.response?.data?.message || "Failed to reject request.");
        }
    };

    // ✅ Columns
    const columns = [

        {
            title: "Outgoing HOD",
            key: "outgoing_hod",
            render: (_, record) => record.outgoing_hod_name || record.outgoing_hod || "-",
        },
        {
            title: "Incoming HOD",
            key: "incoming_hod",
            render: (_, record) => record.incoming_hod_name || record.incoming_hod || "-",
        },
        {
            title: "Requested By",
            key: "requesting_admin",
            render: (_, record) => record.requesting_admin_name || record.requesting_admin || "-",
        },
        {
            title: "Transferred Employees",
            key: "target_employees",
            render: (_, record) => {
                const employees = record.target_employees || [];
                const count = record.total_count ?? employees.length;
                if (count === 0) return "-";
                return (
                    <Button

                        style={{ padding: 0 }}
                        onClick={() => {
                            setSubsRecord(record);
                            setIsSubsModalVisible(true);
                        }}
                    >
                        ({count}) employees
                    </Button>
                );
            },
        },
        {
            title: "Requested On",
            dataIndex: "created_at",
            key: "created_at",
            render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
        },
        {
            title: "Approved On",
            dataIndex: "processed_at",
            key: "processed_at",
            render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
        },
        {
            title: "Approved By",
            key: "approved_by",
            render: (_, record) => record.approved_by_name || record.approved_by || "-",
        },
        // {
        //     title: "Rejection Reason",
        //     dataIndex: "rejection_reason",
        //     key: "rejection_reason",
        //     width: 250,
        //     render: (text) =>
        //         text ? (
        //             <div style={{ whiteSpace: "normal", wordWrap: "break-word", maxWidth: "250px", color: "red" }}>
        //                 {text}
        //             </div>
        //         ) : "-",
        // },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (text) => (
                <Tag color={statusColors[text] || "default"}>{text || "-"}</Tag>
            ),
        },
        ...(authData?.employee?.is_superuser
            ? [{
                title: "Actions",
                key: "actions",
                render: (_, record) => {
                    if (record.status !== "PENDING") return "-";

                    return (
                        <div style={{ display: "flex", gap: "8px" }}>
                            <Button
                                type="text"
                                style={{ color: "green" }}
                                icon={<CheckOutlined />}
                                onClick={() => {
                                    setSelectedRecord(record);
                                    setIsApproveModalVisible(true);
                                }}
                            />
                            <Button
                                type="text"
                                danger
                                icon={<CloseOutlined />}
                                onClick={() => {
                                    setSelectedRecord(record);
                                    setIsRejectModalVisible(true);
                                }}
                            />
                        </div>
                    );
                },
            }]
            : []),
    ];

    // ✅ Filter logic
    const years = Array.from(
        new Set(
            requests
                .filter((item) => item.created_at)
                .map((item) => dayjs(item.created_at).year())
        )
    ).sort((a, b) => b - a);

    const filteredData = requests.filter((item) => {
        const date = dayjs(item.created_at);
        const matchStatus = filters.status ? item.status === filters.status : true;
        const matchYear = filters.year ? date.year() === filters.year : true;
        return matchStatus && matchYear;
    });

    return (
        <>
            {/* ✅ Approve Modal */}
            <Modal
                title="Approve HOD Transfer"
                open={isApproveModalVisible}
                onOk={handleApprove}
                onCancel={() => {
                    setIsApproveModalVisible(false);
                    setAdminRemarks("");
                    setSelectedRecord(null);
                }}
                okText="Approve"
                cancelText="Close"
                okButtonProps={{ style: { backgroundColor: "green", borderColor: "green" } }}
            >
                <p>
                    Are you sure you want to <strong>approve</strong> this HOD transfer request
                    {selectedRecord?.id ? ` (ID: #${selectedRecord.id})` : ""}?
                </p>
                <Input.TextArea
                    rows={4}
                    placeholder="Enter reason for approval"
                    value={adminRemarks}
                    onChange={(e) => setAdminRemarks(e.target.value)}
                />
            </Modal>

            {/* ✅ Reject Modal */}
            <Modal
                title="Reject HOD Transfer"
                open={isRejectModalVisible}
                onOk={handleReject}
                onCancel={() => {
                    setIsRejectModalVisible(false);
                    setAdminRemarks("");
                    setSelectedRecord(null);
                }}
                okText="Reject"
                cancelText="Close"
                okButtonProps={{ danger: true }}
            >
                <p>
                    Are you sure you want to <strong>reject</strong> this HOD transfer request
                    {selectedRecord?.id ? ` (ID: #${selectedRecord.id})` : ""}?
                </p>
                <Input.TextArea
                    rows={4}
                    placeholder="Enter reason for rejection"
                    value={adminRemarks}
                    onChange={(e) => setAdminRemarks(e.target.value)}
                />
            </Modal>

            {/* ✅ Transferred Employees Modal */}
            <Modal
                title={`Transferred Employees ${subsRecord ? `(${subsRecord.total_count ?? subsRecord.target_employees?.length ?? 0})` : ""}`}
                open={isSubsModalVisible}
                onCancel={() => {
                    setIsSubsModalVisible(false);
                    setSubsRecord(null);
                }}
                footer={
                    <Button onClick={() => { setIsSubsModalVisible(false); setSubsRecord(null); }}>
                        Close
                    </Button>
                }
            >
                {subsRecord?.target_employees?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px", padding: "8px 0" }}>
                        {subsRecord.target_employees.map((emp, i) => (
                            <div
                                key={i}
                                style={{
                                    background: "#f8fafd",
                                    border: "1px solid #dde3ed",
                                    borderRadius: 8,
                                    padding: "10px 14px",
                                }}
                            >
                                <div style={{ fontWeight: 600, fontSize: 13 }}>{emp.first_name}</div>
                                <div style={{ fontSize: 12, color: "#8fa3bc" }}>{emp.emp_code}</div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p style={{ color: "#8fa3bc", textAlign: "center" }}>No employees listed.</p>
                )}
            </Modal>

            {/* ✅ Filters */}
            <div
                style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: "10px",
                    marginBottom: "16px",
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: "center",
                }}
            >
                <Select
                    placeholder="Filter by Year"
                    style={{ width: isMobile ? "100%" : 140 }}
                    allowClear
                    onChange={(value) => setFilters((prev) => ({ ...prev, year: value }))}
                >
                    {years.map((year) => (
                        <Select.Option key={year} value={year}>
                            {year}
                        </Select.Option>
                    ))}
                </Select>

                <Select
                    placeholder="Filter by Status"
                    style={{ width: isMobile ? "100%" : 200 }}
                    allowClear
                    onChange={(value) => setFilters((prev) => ({ ...prev, status: value }))}
                >
                    {["PENDING", "APPROVED", "REJECTED", "CANCELLED"].map((status) => (
                        <Select.Option key={status} value={status}>
                            {status}
                        </Select.Option>
                    ))}
                </Select>
            </div>

            {/* ✅ Table */}
            <Table
                columns={columns}
                dataSource={filteredData.map((item, index) => ({
                    key: index,
                    ...item,
                }))}
                pagination={{ pageSize: 5 }}
                scroll={{ x: true }}
                components={{
                    header: {
                        cell: (props) => (
                            <th
                                {...props}
                                style={{
                                    backgroundColor: "#0e497b",
                                    color: "white",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {props.children}
                            </th>
                        ),
                    },
                }}
                bordered
            />
        </>
    );
}